/**
 * 用户界面组件
 * 负责管理 UI 交互和状态
 */

import { CurlParser } from '../parser/curlParser.js';
import { LanguageLoader } from '../utils/languageLoader.js';
import { copyToClipboard, debounce } from '../utils/helpers.js';
import { examples } from '../../data/examples.js';
import { languages } from '../../data/languages.js';

export class UIController {
  constructor() {
    this.parser = new CurlParser();
    this.languageLoader = new LanguageLoader();
    this.currentLanguage = 'python';
    this.currentExample = null;

    // DOM 元素引用
    this.elements = {};

    // 事件处理器
    this.handlers = {};

    // 状态
    this.state = {
      isLoading: false,
      hasError: false,
      lastGeneratedCode: '',
      theme: 'light'
    };

    // 防抖的转换函数
    this.debouncedConvert = debounce(this.convert.bind(this), 300);
  }

  /**
   * 初始化 UI 控制器
   * @returns {Promise<void>}
   */
  async init() {
    try {
      this.cacheElements();
      this.setupEventListeners();
      await this.languageLoader.initialize();
      this.loadExamples();
      this.loadLanguageSelector();
      this.setupTheme();
      this.showWelcomeMessage();

      console.log('UI 控制器初始化完成');
    } catch (error) {
      console.error('UI 控制器初始化失败:', error);
      this.showError('应用初始化失败，请刷新页面重试');
    }
  }

  /**
   * 缓存 DOM 元素
   */
  cacheElements() {
    this.elements = {
      // 输入区域
      curlInput: document.getElementById('curl-input'),
      clearBtn: document.getElementById('clear-btn'),
      pasteBtn: document.getElementById('paste-btn'),
      inputError: document.getElementById('input-error'),
      charCount: document.getElementById('char-count'),

      // 输出区域
      languageSelect: document.getElementById('language-select'),
      copyBtn: document.getElementById('copy-btn'),
      codeOutput: document.getElementById('code-output'),
      outputError: document.getElementById('output-error'),

      // 示例区域
      examplesToggle: document.getElementById('examples-toggle'),
      examplesSection: document.getElementById('examples-section'),
      examplesContainer: document.getElementById('examples-container'),
      examplesClose: document.getElementById('examples-close'),

      // 主题和模态框
      themeToggle: document.getElementById('theme-toggle'),
      aboutModal: document.getElementById('about-modal'),
      aboutClose: document.getElementById('about-close'),
      loadingModal: document.getElementById('loading-modal'),
      generatedCode: document.getElementById('generated-code')
    };
  }

  /**
   * 设置事件监听器
   */
  setupEventListeners() {
    // 输入事件
    this.elements.curlInput.addEventListener('input', this.handleInputChange.bind(this));
    this.elements.clearBtn.addEventListener('click', this.handleClear.bind(this));
    this.elements.pasteBtn.addEventListener('click', this.handlePaste.bind(this));

    // 输出事件
    this.elements.languageSelect.addEventListener('change', this.handleLanguageChange.bind(this));
    this.elements.copyBtn.addEventListener('click', this.handleCopy.bind(this));

    // 示例事件
    this.elements.examplesToggle.addEventListener('click', this.handleExamplesToggle.bind(this));
    this.elements.examplesClose.addEventListener('click', this.handleExamplesClose.bind(this));

    // 主题和模态框事件
    if (this.elements.themeToggle) {
      this.elements.themeToggle.addEventListener('click', this.handleThemeToggle.bind(this));
    }
    if (this.elements.aboutClose) {
      this.elements.aboutClose.addEventListener('click', this.handleAboutClose.bind(this));
    }

    // 全局事件
    document.addEventListener('keydown', this.handleKeydown.bind(this));
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  /**
   * 处理输入变化
   * @param {Event} event - 输入事件
   */
  handleInputChange(event) {
    const input = event.target.value;
    this.updateCharCount(input);
    this.debouncedConvert(input);
  }

  /**
   * 处理清空按钮
   */
  handleClear() {
    this.elements.curlInput.value = '';
    this.elements.curlInput.focus();
    this.clearOutput();
    this.clearErrors();
    this.updateCharCount('');
  }

  /**
   * 处理粘贴按钮
   */
  async handlePaste() {
    try {
      const text = await navigator.clipboard.readText();
      this.elements.curlInput.value = text;
      this.elements.curlInput.focus();
      this.updateCharCount(text);
      this.convert(text);
    } catch (error) {
      console.warn('无法访问剪贴板:', error);
      // 降级到提示用户手动粘贴
      this.elements.curlInput.focus();
    }
  }

  /**
   * 处理语言选择变化
   * @param {Event} event - 选择事件
   */
  handleLanguageChange(event) {
    const newLanguage = event.target.value;
    this.setCurrentLanguage(newLanguage);
    const currentInput = this.elements.curlInput.value;
    if (currentInput.trim()) {
      this.convert(currentInput);
    }
  }

  /**
   * 处理复制按钮
   */
  async handleCopy() {
    const code = this.state.lastGeneratedCode;
    if (!code) {
      this.showError('没有可复制的代码');
      return;
    }

    try {
      const success = await copyToClipboard(code);
      if (success) {
        this.showSuccess('代码已复制到剪贴板');
        this.animateCopyButton();
      } else {
        throw new Error('复制失败');
      }
    } catch (error) {
      console.error('复制失败:', error);
      this.showError('复制失败，请手动选择并复制代码');
    }
  }

  /**
   * 处理示例切换
   */
  handleExamplesToggle() {
    const isVisible = this.elements.examplesSection.style.display !== 'none';
    if (isVisible) {
      this.hideExamples();
    } else {
      this.showExamples();
    }
  }

  /**
   * 处理示例关闭
   */
  handleExamplesClose() {
    this.hideExamples();
  }

  /**
   * 处理主题切换
   */
  handleThemeToggle() {
    this.toggleTheme();
  }

  /**
   * 处理关于对话框关闭
   */
  handleAboutClose() {
    this.hideAboutModal();
  }

  /**
   * 处理键盘事件
   * @param {KeyboardEvent} event - 键盘事件
   */
  handleKeydown(event) {
    // Ctrl/Cmd + Enter 触发转换
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      const input = this.elements.curlInput.value;
      if (input.trim()) {
        this.convert(input);
      }
    }

    // Escape 键关闭模态框
    if (event.key === 'Escape') {
      this.hideExamples();
      this.hideAboutModal();
    }
  }

  /**
   * 处理外部点击
   * @param {MouseEvent} event - 鼠标事件
   */
  handleOutsideClick(event) {
    // 点击外部关闭示例区域
    if (this.elements.examplesSection.style.display !== 'none' &&
        !this.elements.examplesSection.contains(event.target) &&
        event.target !== this.elements.examplesToggle) {
      this.hideExamples();
    }
  }

  /**
   * 转换 curl 命令
   * @param {string} curlCommand - curl 命令
   */
  async convert(curlCommand) {
    if (!curlCommand.trim()) {
      this.clearOutput();
      return;
    }

    this.setLoading(true);
    this.clearErrors();

    try {
      // 解析 curl 命令
      const parsed = this.parser.parse(curlCommand);

      // 生成代码
      const generator = await this.languageLoader.loadGenerator(this.currentLanguage);
      const code = generator.generate(parsed);

      // 显示结果
      this.displayCode(code, this.currentLanguage);
      this.state.lastGeneratedCode = code;

    } catch (error) {
      console.error('转换失败:', error);
      this.showError(`转换失败: ${error.message}`);
      this.clearOutput();
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * 显示生成的代码
   * @param {string} code - 生成的代码
   * @param {string} language - 语言 ID
   */
  displayCode(code, language) {
    const languageConfig = languages.find(lang => lang.id === language);
    const highlightLanguage = languageConfig ? languageConfig.highlight : 'plaintext';

    // 确保 generatedCode 元素存在
    if (!this.elements.generatedCode) {
      console.error('generatedCode 元素不存在');
      return;
    }

    this.elements.generatedCode.textContent = code;
    this.elements.generatedCode.className = `language-${highlightLanguage}`;

    // 触发语法高亮
    if (window.Prism) {
      window.Prism.highlightElement(this.elements.generatedCode);
    }
  }

  /**
   * 清空输出
   */
  clearOutput() {
    if (this.elements.generatedCode) {
      this.elements.generatedCode.textContent = '// 在左侧输入 curl 命令以生成代码';
      this.elements.generatedCode.className = 'language-python';
    }
    this.state.lastGeneratedCode = '';
  }

  /**
   * 设置当前语言
   * @param {string} languageId - 语言 ID
   */
  setCurrentLanguage(languageId) {
    this.currentLanguage = languageId;
    this.elements.languageSelect.value = languageId;
  }

  /**
   * 加载示例
   */
  loadExamples() {
    const container = this.elements.examplesContainer;
    container.innerHTML = '';

    examples.forEach(example => {
      const exampleElement = this.createExampleElement(example);
      container.appendChild(exampleElement);
    });
  }

  /**
   * 创建示例元素
   * @param {Object} example - 示例对象
   * @returns {HTMLElement} 示例元素
   */
  createExampleElement(example) {
    const div = document.createElement('div');
    div.className = 'example-item';
    div.onclick = () => this.loadExample(example);

    div.innerHTML = `
      <div class="example-title">
        <span class="example-icon">${example.icon}</span>
        ${example.title}
      </div>
      <div class="example-command">${this.truncateCommand(example.command)}</div>
    `;

    return div;
  }

  /**
   * 截断命令显示
   * @param {string} command - 命令
   * @returns {string} 截断后的命令
   */
  truncateCommand(command) {
    if (command.length <= 60) {
      return command;
    }
    return command.substring(0, 57) + '...';
  }

  /**
   * 加载示例
   * @param {Object} example - 示例对象
   */
  loadExample(example) {
    this.elements.curlInput.value = example.command;
    this.updateCharCount(example.command);
    this.convert(example.command);
    this.hideExamples();
    this.currentExample = example;
  }

  /**
   * 加载语言选择器
   */
  loadLanguageSelector() {
    const select = this.elements.languageSelect;
    select.innerHTML = '';

    // 只显示已实现的语言
    const implementedLanguages = ['python', 'javascript', 'nodejs', 'php', 'java', 'go'];
    
    languages
      .filter(language => implementedLanguages.includes(language.id))
      .forEach(language => {
        const option = document.createElement('option');
        option.value = language.id;
        option.textContent = language.displayName;
        select.appendChild(option);
      });

    // 设置默认语言
    select.value = this.currentLanguage;
  }

  /**
   * 显示示例
   */
  showExamples() {
    this.elements.examplesSection.classList.remove('hidden');
    this.elements.examplesSection.style.display = 'block';
  }

  /**
   * 隐藏示例
   */
  hideExamples() {
    this.elements.examplesSection.classList.add('hidden');
    this.elements.examplesSection.style.display = 'none';
  }

  /**
   * 显示关于模态框
   */
  showAboutModal() {
    this.elements.aboutModal.style.display = 'flex';
  }

  /**
   * 隐藏关于模态框
   */
  hideAboutModal() {
    this.elements.aboutModal.style.display = 'none';
  }

  /**
   * 设置加载状态
   * @param {boolean} loading - 是否正在加载
   */
  setLoading(loading) {
    this.state.isLoading = loading;

    if (this.elements.loadingModal) {
      if (loading) {
        this.elements.loadingModal.style.display = 'flex';
      } else {
        this.elements.loadingModal.style.display = 'none';
      }
    }
  }

  /**
   * 显示错误信息
   * @param {string} message - 错误消息
   * @param {string} type - 错误类型 ('input' | 'output')
   */
  showError(message, type = 'output') {
    const errorElement = type === 'input' ?
      this.elements.inputError : this.elements.outputError;

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
      this.state.hasError = true;

      // 3秒后自动隐藏
      setTimeout(() => {
        this.hideError(type);
      }, 3000);
    } else {
      console.error(message);
    }
  }

  /**
   * 隐藏错误信息
   * @param {string} type - 错误类型
   */
  hideError(type = 'output') {
    const errorElement = type === 'input' ?
      this.elements.inputError : this.elements.outputError;

    if (errorElement) {
      errorElement.style.display = 'none';
      this.state.hasError = false;
    }
  }

  /**
   * 清空所有错误
   */
  clearErrors() {
    if (this.elements.inputError || this.elements.outputError) {
      this.hideError('input');
      this.hideError('output');
    }
  }

  /**
   * 显示成功消息
   * @param {string} message - 成功消息
   */
  showSuccess(message) {
    // 临时显示在输出错误区域
    const successElement = this.elements.outputError;
    successElement.textContent = message;
    successElement.className = 'success-message';
    successElement.style.display = 'block';

    setTimeout(() => {
      successElement.style.display = 'none';
      successElement.className = 'error-message';
    }, 2000);
  }

  /**
   * 更新字符计数
   * @param {string} text - 文本内容
   */
  updateCharCount(text) {
    const count = text.length;
    this.elements.charCount.textContent = `${count} 字符`;
  }

  /**
   * 设置主题
   */
  setupTheme() {
    const savedTheme = localStorage.getItem('theme') || 'light';
    this.state.theme = savedTheme;
    this.applyTheme(savedTheme);
  }

  /**
   * 切换主题
   */
  toggleTheme() {
    const newTheme = this.state.theme === 'light' ? 'dark' : 'light';
    this.state.theme = newTheme;
    this.applyTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  }

  /**
   * 应用主题
   * @param {string} theme - 主题名称
   */
  applyTheme(theme) {
    if (theme === 'dark') {
      document.body.classList.add('dark-theme');
      document.body.style.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)';
    } else {
      document.body.classList.remove('dark-theme');
      document.body.style.background = '';
    }

    // 更新按钮图标
    if (this.elements.themeToggle) {
      const icon = this.elements.themeToggle.querySelector('i');
      if (icon) {
        icon.className = theme === 'dark' ? 'fas fa-sun' : 'fas fa-moon';
      }
    }
  }

  /**
   * 设置主题（保留兼容性）
   * @param {string} theme - 主题名称
   */
  setTheme(theme) {
    this.state.theme = theme;
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
  }

  /**
   * 复制按钮动画
   */
  animateCopyButton() {
    const button = this.elements.copyBtn;
    button.textContent = '✅ 已复制';
    button.classList.add('success');

    setTimeout(() => {
      button.textContent = '📋 复制';
      button.classList.remove('success');
    }, 2000);
  }

  /**
   * 显示欢迎消息
   */
  showWelcomeMessage() {
    // 可以在这里显示一些欢迎提示或使用说明
    console.log('欢迎使用 Curl 转换器！');
  }

  /**
   * 获取当前状态
   * @returns {Object} 当前状态
   */
  getState() {
    return { ...this.state };
  }

  /**
   * 销毁控制器
   */
  destroy() {
    // 清理事件监听器
    Object.values(this.elements).forEach(element => {
      if (element && element.removeEventListener) {
        // 移除所有事件监听器（简化版）
        element.replaceWith(element.cloneNode(true));
      }
    });

    // 清理状态
    this.state = {};
    this.elements = {};
  }
}

// 全局函数用于模态框
window.showAbout = function() {
  const modal = document.getElementById('about-modal');
  if (modal) {
    modal.style.display = 'flex';
  }
};